import { useMemo } from 'react';
import { cn } from '@/lib/utils';

/** Deterministic PRNG so the star layout never shifts between renders. */
function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Star {
  left: string;
  top: string;
  size: number;
  delay: string;
  duration: string;
}

interface StarfieldProps {
  count?: number;
  seed?: number;
  className?: string;
}

export function Starfield({ count = 42, seed = 7, className }: StarfieldProps) {
  const stars = useMemo<Star[]>(() => {
    const rand = mulberry32(seed);
    return Array.from({ length: count }, () => ({
      left: `${(rand() * 100).toFixed(2)}%`,
      top: `${(rand() * 100).toFixed(2)}%`,
      size: 1 + Math.round(rand() * 2),
      delay: `${(rand() * 4).toFixed(2)}s`,
      duration: `${(2.5 + rand() * 3).toFixed(2)}s`,
    }));
  }, [count, seed]);

  return (
    <div className={cn('pointer-events-none absolute inset-0', className)} aria-hidden="true">
      {stars.map((star, i) => (
        <span
          key={i}
          className="absolute animate-twinkle rounded-full bg-foreground/70"
          style={{
            left: star.left,
            top: star.top,
            width: star.size,
            height: star.size,
            animationDelay: star.delay,
            animationDuration: star.duration,
          }}
        />
      ))}
    </div>
  );
}
